// 騎士の踏み込み (突き・薙ぎの active の前進) の蹴り出しの砂埃と、床の擦り跡 (2026-09-27)。
//   ユーザー逐語 (2026-09-27 検収パック R-2 薙ぎの届き):「絵と合う」。メモ「ただ、離れた時に遠くまで同じ姿勢で
//     接近してくるのが不自然だから、エフェクトで前に蹴り出しているようなエフェクトを付けてごまかして欲しい。砂埃的な」
//
// 何を出すか (描くだけ・判定と移動は1pxも変えない):
//   ① 蹴り出し: active に入った tick に、後ろ足から後ろへ砂埃が弾ける。量は踏み込む距離で決まる
//      (MIN_PX 未満 = 出さない / FULL_PX で最大)。石くずが数粒、低く後ろへ飛ぶ。
//   ② 擦り跡: 踏み込みで実際に滑った道のり (tick ごとの前後の x) に、床を擦った薄い筋と低い土煙を残す。
//      主人公の芯で止まった時も、実際に動いた所にだけ出る。
//   🔵 2026-09-28 濃くした (検収パック M-3「もう少しエフェクトは濃くて良い」): 土煙の不透明度 約1.45倍・粒 +1〜2・
//      大きさ 1.2倍・寿命 0.46→0.5 秒、擦り跡の筋と土煙も約1.5倍。出す条件・位置・量の決め方は同じ。
//   色 = 騎士の広間の床 (石の青灰 rgb 50-65,55-70,70-90 を knight-hall-v2.png から実測) より一段明るい、くすんだ青灰。
//
// 切り替え (DEV のみ URL): `?knightDust=off` = 何も出さない / `?knightDust=drawing` = 楕円の描き方 (09-28 までの既定)。
//   🔵 2026-09-28 見本の絵から作る2版 (knight_dust_art.ts・台帳 production.md「FXの作り方」):
//     `?knightDust=painted` = 見本の6コマを貼る / `?knightDust=code` = 見本から切り出した塊を動かす。
//     Sol の粒の版 (`particle`) は見本なしで作られた物なので置き換えた (`particle` は `code` の別名として残す)。
//   🔵 2026-09-28 既定 = code (検収パック 09-28c D-1「コードの版」・台帳 bosses.md)。材料が読み込まれる前は楕円で描く。
import type { Boss, FxEvent } from './types'
import { getKnightMove } from './knight_gait'
import { drawKnightDustCoded, drawKnightDustPainted } from './knight_dust_art'
import { knightHelmetScaleEnabled, knightHelmetScaleFactor } from './knight_helmet_scale'

export type KnightDustMode = 'drawing' | 'painted' | 'code' | 'off'
let dustMode: KnightDustMode = 'code'
export function getKnightLungeDust(): boolean { return dustMode !== 'off' }
export function setKnightLungeDust(on: boolean): void { dustMode = on ? 'code' : 'off' }
export function getKnightDustMode(): KnightDustMode { return dustMode }
export function setKnightDustMode(mode: KnightDustMode): void { dustMode = mode }

const LUNGE_ATTACKS = new Set(['lanceThrust', 'lanceSweep'])
/** これより短い踏み込みには出さない (その場の振りに砂埃は要らない)。 */
export const KNIGHT_LUNGE_DUST_MIN_PX = 40
/** この距離で最大の量。 */
export const KNIGHT_LUNGE_DUST_FULL_PX = 170
// 後ろ足の位置 (ボス中心から後ろへ・ゲーム px)。active の最初の絵 (突き = lanceThrust_full / 薙ぎ = lanceSweep_contact)
//   の後ろ足のつま先を実機の描画矩形から実測した値 (scripts/probe_knight_feet.mjs)。
//   値は旧の大きさ (DEV ?knightScale=old) での実測。10-01 から兜で揃えた大きさが既定なので、その絵の倍率を掛けて使う
//   (後ろ足のつま先は本体中心からの距離なので倍率どおりに伸びる: 突き 98→105.4 / 薙ぎ 72→82.8)。
export const KNIGHT_LUNGE_REAR_FOOT_PX: Record<string, number> = { lanceThrust: 98, lanceSweep: 72 }
const LUNGE_FIRST_IMAGE: Record<string, string> = { lanceThrust: 'lanceThrust_full', lanceSweep: 'lanceSweep_contact' }
export function knightLungeRearFootPx(attackId: string): number {
  const base = KNIGHT_LUNGE_REAR_FOOT_PX[attackId] ?? 80
  const image = LUNGE_FIRST_IMAGE[attackId]
  return image ? base * knightHelmetScaleFactor(image, knightHelmetScaleEnabled()) : base
}

export function knightLungeDustAmount(distancePx: number): number {
  if (distancePx < KNIGHT_LUNGE_DUST_MIN_PX) return 0
  return Math.min(1, 0.35 + 0.65 * (distancePx - KNIGHT_LUNGE_DUST_MIN_PX) / (KNIGHT_LUNGE_DUST_FULL_PX - KNIGHT_LUNGE_DUST_MIN_PX))
}

function isKnightLunge(b: Boss): boolean {
  return b.id === 'vigil-knight' && b.state === 'active' && !!b.currentAttack && LUNGE_ATTACKS.has(b.currentAttack.id)
}

/** game.ts から毎 tick。prev = この tick の前のボス、next = 後のボス。 */
export function knightLungeDustFx(prev: Boss, next: Boss, now: number): FxEvent[] {
  if (dustMode === 'off' || getKnightMove() === 'old' || !isKnightLunge(next)) return []
  const dir = next.facing === 'right' ? 1 : -1
  const out: FxEvent[] = []
  const entered = prev.state !== 'active' || prev.currentAttack?.id !== next.currentAttack?.id
    || (prev.comboHitIndex ?? 0) !== (next.comboHitIndex ?? 0)
  if (entered) {
    const planned = Math.abs(next.lungeTargetX - next.x)
    const amount = knightLungeDustAmount(planned)
    if (amount > 0) {
      const rear = knightLungeRearFootPx(next.currentAttack!.id)
      out.push({ kind: 'knight_lunge_dust', variant: 'kick', x: next.x - dir * rear, y: next.y, t: now, dir, amount })
    }
  }
  // 擦り跡: この tick に実際に滑った分。踏み込みが短い技 (最大量 0) には出さない。
  const moved = next.x - prev.x
  if (!entered && Math.abs(moved) > 0.5 && Math.sign(moved) === dir) {
    const total = Math.abs(next.lungeTargetX - next.actionAnchorX)
    const amount = knightLungeDustAmount(Math.max(total, Math.abs(next.x - next.actionAnchorX)))
    if (amount > 0) {
      const rear = knightLungeRearFootPx(next.currentAttack!.id)
      out.push({ kind: 'knight_lunge_dust', variant: 'scuff', x: prev.x - dir * rear, x2: next.x - dir * rear, y: next.y, t: now, dir, amount })
    }
  }
  return out
}

// ===================================================================================
// 描画 (render.ts drawFx から)。age = 秒。FX 共通の寿命 0.5 秒に収める。
// ===================================================================================
const hash01 = (n: number): number => { const s = Math.sin(n * 127.1 + 311.7) * 43758.5453; return s - Math.floor(s) }
const DUST = [118, 122, 138] as const    // 床より一段明るい青灰
const DUST_DARK = [70, 72, 86] as const
const GRIT = [150, 150, 164] as const
const rgba = (c: readonly [number, number, number], a: number) => `rgba(${c[0]},${c[1]},${c[2]},${Math.max(0, Math.min(1, a)).toFixed(3)})`

export function drawKnightLungeDust(
  ctx: CanvasRenderingContext2D, f: Extract<FxEvent, { kind: 'knight_lunge_dust' }>, age: number,
): void {
  if (dustMode === 'painted' && drawKnightDustPainted(ctx, f, age)) return
  if (dustMode === 'code' && drawKnightDustCoded(ctx, f, age)) return
  if (f.variant === 'scuff') { drawScuff(ctx, f, age); return }
  const life = 0.5
  if (age > life) return
  const k = age / life
  const fade = (1 - k) * (1 - k)
  const seed = f.t * 91.7 + f.x
  const a = f.amount
  ctx.save()
  // 土煙: 後ろ足から後ろへ低く膨らむ楕円 3〜5 個 (量で個数と大きさ)。
  const puffs = 4 + Math.round(3 * a)
  for (let i = 0; i < puffs; i++) {
    const h = hash01(seed + i * 3.1), h2 = hash01(seed + i * 5.7 + 2)
    const grow = 1 - (1 - Math.min(1, age / 0.18)) ** 2
    const back = (10 + 46 * a * (0.5 + h)) * grow + 14 * i * a
    const rise = (6 + 14 * a * h2) * grow
    const r = 1.2 * (8 + 16 * a * (0.6 + h)) * (0.55 + 0.6 * grow)
    const x = f.x - f.dir * back
    const y = f.y - 4 - rise
    ctx.fillStyle = rgba(i % 2 ? DUST_DARK : DUST, (0.6 + 0.26 * a) * fade * (1 - i * 0.07))
    ctx.beginPath()
    ctx.ellipse(x, y, r * 1.35, r * 0.62, -f.dir * 0.12, 0, Math.PI * 2)
    ctx.fill()
  }
  // 石くず: 後ろ・上へ弾け、重力で落ちる。
  const grits = 4 + Math.round(5 * a)
  for (let i = 0; i < grits; i++) {
    const h = hash01(seed + i * 7.3), h2 = hash01(seed + i * 2.9 + 11)
    const ang = 0.25 + h * 0.75            // 後ろ斜め上 (0 = 真後ろの水平)
    const v = (120 + 170 * a) * (0.5 + h2 * 0.7)
    const x = f.x - f.dir * Math.cos(ang) * v * age
    const y = f.y - 3 - Math.sin(ang) * v * age + 700 * age * age
    if (y > f.y + 2) continue
    const sz = 1.6 + h2 * 2.4
    ctx.fillStyle = rgba(GRIT, 0.9 * (1 - k))
    ctx.fillRect(x - sz / 2, y - sz / 2, sz, sz)
  }
  ctx.restore()
}

function drawScuff(ctx: CanvasRenderingContext2D, f: Extract<FxEvent, { kind: 'knight_lunge_dust' }>, age: number): void {
  const life = 0.5
  if (age > life || f.x2 === undefined) return
  const k = age / life
  const fade = (1 - k) * (1 - k)
  const a = f.amount
  ctx.save()
  // 床の擦り筋 (細い2本)。
  ctx.lineCap = 'round'
  ctx.strokeStyle = rgba(DUST, 0.75 * a * fade)
  ctx.lineWidth = 2
  ctx.beginPath(); ctx.moveTo(f.x, f.y + 1); ctx.lineTo(f.x2, f.y + 1); ctx.stroke()
  ctx.strokeStyle = rgba(DUST_DARK, 0.68 * a * fade)
  ctx.lineWidth = 1.5
  ctx.beginPath(); ctx.moveTo(f.x, f.y + 3.5); ctx.lineTo(f.x2, f.y + 3.5); ctx.stroke()
  // 擦った所から上がる低い土煙 (1区間に1つ)。
  const mid = (f.x + f.x2) / 2
  const r = 1.2 * (5 + 7 * a) * (0.6 + 0.8 * Math.min(1, age / 0.25))
  ctx.fillStyle = rgba(DUST, 0.4 * a * fade)
  ctx.beginPath()
  ctx.ellipse(mid - f.dir * 4 * k, f.y - 3 - 5 * k, r * 1.4, r * 0.5, 0, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()
}

if (import.meta.env.DEV && typeof window !== 'undefined') {
  const v = new URLSearchParams(window.location.search).get('knightDust')
  if (v === 'off') setKnightLungeDust(false)
  if (v === 'drawing') setKnightDustMode('drawing')
  if (v === 'painted') setKnightDustMode('painted')
  if (v === 'code' || v === 'particle') setKnightDustMode('code')
}
